/**
 * ConfirmDialog — a minimal, token-styled confirmation modal. Originally
 * replaced the Bootstrap `#confirmationModal` from nuadvisory's
 * `ClosingEmailAdmin.cshtml` that gated Save and Delete.
 *
 * Lives in `packages/web/src/components/` (web-shared, not a `@nu-assist/ui`
 * primitive): it now has 3 consumers across two features (closing's Save/Delete
 * + Send, and operations' destructive Searches Import), so it's promoted out of
 * `routes/closing/` to a shared location. It is NOT yet a `ui` primitive because
 * there is no Radix Dialog dependency in the repo; if a 4th surface or the
 * extension needs it, promote to a `@radix-ui/react-dialog`-backed `ui`
 * primitive.
 *
 * A11y: `role="dialog"` + `aria-modal` + `aria-labelledby` (a per-instance id,
 * so two dialogs never share one), ESC cancels, backdrop click cancels, Tab /
 * Shift+Tab cycle inside the dialog, and focus returns to whatever had it
 * before the dialog opened. The confirm button takes focus on open; pass
 * `initialFocus="cancel"` when the action cannot be undone, so an Enter that
 * opened the dialog cannot also confirm it.
 */
import { Button } from "@nu-assist/ui";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef } from "react";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  /** Style the confirm action as destructive (red). */
  destructive?: boolean;
  busy?: boolean;
  /** Which button has focus when the dialog opens (default: the confirm action). */
  initialFocus?: "confirm" | "cancel";
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  destructive = false,
  busy = false,
  initialFocus = "confirm",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    // Read who had focus BEFORE moving it in (an `autoFocus` would already
    // have moved it by the time an effect runs); it gets focus back on close.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLElement>("[data-initial-focus]")?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={onCancel}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a role="dialog" div is the
          Radix-style modal shape; native <dialog>'s showModal() imperative API
          fights React conditional mounting + happy-dom's partial support. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        onKeyDown={trapTab}
        className="w-full max-w-md rounded-lg border border-border bg-surface p-5 shadow-lg"
        // Stop a backdrop-cancel when the click starts inside the dialog.
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-h3 text-text">
          {title}
        </h2>
        <p className="mt-2 text-body text-text-secondary">{message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            data-initial-focus={initialFocus === "cancel" ? "" : undefined}
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            data-initial-focus={initialFocus === "confirm" ? "" : undefined}
            variant={destructive ? "danger" : "primary"}
            type="button"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
